/**
 * Mi tienda's "Guía de inicio" (docs/SELLER_ONBOARDING_PLAN.md): one list that takes a shop from its
 * request to its first sale. Every task is worked out from what the shop already has, so nobody ticks
 * anything; the panel shows it, the server checks it before the shop closes it, and Admin › Mercado ›
 * Pendientes shows how far a shop under review got.
 */
import { deliveryModesOf, isInPerson, type DeliveryMode, type DeliveryOptions, type MarketPaymentMethod, type SellerKind, type SellerStatus } from "./market";
import type { SellerSection } from "./sellerPaths";

/** Live listings a shop needs before its first sale for the guide to call it ready. */
export const ONBOARDING_LISTINGS = 3;
/** A bio this long tells buyers who's behind the shop. */
export const ONBOARDING_BIO_CHARS = 40;

/**
 * How the shop can actually sell, as the market decides it: a limited shop only hands over in person,
 * and cash only counts with an in-person delivery.
 */
export function sellerSetup(s: { delivery?: DeliveryOptions | null; payments?: MarketPaymentMethod[] | null; limited?: boolean }): { modes: DeliveryMode[]; payments: MarketPaymentMethod[] } {
  const modes = (s.delivery ? deliveryModesOf(s.delivery) : []).filter((m) => !s.limited || m === "hand");
  return { modes, payments: (s.payments ?? []).filter((p) => p !== "cash" || modes.some(isInPerson)) };
}

export type OnboardingTaskKey = "photo" | "payment" | "delivery" | "first_listing" | "bio" | "listings" | "mp" | "verify";

export interface OnboardingTask {
  key: OnboardingTaskKey;
  required: boolean;
  done: boolean;
  /** Only the shop's owner can do it (payments, Mercado Pago, asking to be verified): the admin working as the shop can't. */
  ownerOnly: boolean;
  /** Where it's done: a section of the panel, or the verification box on Inicio. */
  to: SellerSection | "#verificacion";
}

export interface OnboardingView {
  /** "review": under review (A); "first_sale": approved, nothing sold yet (B). */
  phase: "review" | "first_sale";
  tasks: OnboardingTask[];
  done: number;
  /** No required task is left: the shop may close the guide (only after approval). */
  canDismiss: boolean;
  /** Required tasks done out of required tasks: what Admin › Pendientes shows. */
  required: { done: number; total: number };
}

/** What the guide reads from the shop (MySeller on the panel, the seller's row on the server). */
export interface OnboardingSeller {
  status: SellerStatus;
  kind?: SellerKind;
  limited?: boolean;
  bio: string;
  delivery?: DeliveryOptions | null;
  payments?: MarketPaymentMethod[] | null;
  mp: { connected: boolean; available: boolean };
  hasVerificationPhoto?: boolean;
  verification?: { verified: boolean; requestedAt: string | null };
  plan?: { liveListings: number } | null;
  managed?: { active: boolean } | null;
  onboardingDismissed?: boolean;
}

/**
 * The guide for this shop, or null when there's none to show: sold already, not under review nor
 * approved, a shop the admin set up that its owner hasn't activated (they see "Tu tienda está lista"
 * instead; the admin working as it does see the guide), or closed with every required task done.
 * `listings` counts every listing the shop has prepared (under review they can't be live yet).
 */
export function onboardingView(s: OnboardingSeller, ctx: { listings: number; completedSales: number; acting?: boolean }): OnboardingView | null {
  if (s.status !== "pending" && s.status !== "active") return null;
  if (s.managed && !s.managed.active && !ctx.acting) return null;
  const setup = sellerSetup(s);
  const task = (key: OnboardingTaskKey, required: boolean, done: boolean, to: OnboardingTask["to"], ownerOnly = false): OnboardingTask => ({ key, required, done, ownerOnly, to });
  const payment = task("payment", true, setup.payments.length > 0, "ajustes", true);
  const delivery = task("delivery", true, setup.modes.length > 0, "ajustes");
  let tasks: OnboardingTask[];
  let phase: OnboardingView["phase"];
  if (s.status === "pending") {
    phase = "review";
    tasks = [
      // A company applies with its legal name and CUIT instead of the photo.
      ...(s.kind === "company" ? [] : [task("photo", true, Boolean(s.hasVerificationPhoto), "#verificacion", true)]),
      payment,
      delivery,
      task("first_listing", true, ctx.listings > 0, "publicaciones"),
      task("bio", false, (s.bio ?? "").trim().length >= ONBOARDING_BIO_CHARS, "ajustes"),
    ];
  } else {
    if (ctx.completedSales > 0) return null;
    phase = "first_sale";
    tasks = [
      // What it needed to be approved and lost since (or a shop the admin set up, whose owner loads how they get paid): first.
      ...[payment, delivery].filter((t) => !t.done),
      task("listings", true, (s.plan?.liveListings ?? 0) >= ONBOARDING_LISTINGS, "publicar"),
      // Recommended, not required: a limited shop only takes cash.
      ...(s.mp.available && !s.limited ? [task("mp", false, s.mp.connected, "ajustes", true)] : []),
      task("verify", false, Boolean(s.verification?.verified || s.verification?.requestedAt), "ajustes", true),
    ];
  }
  const required = tasks.filter((t) => t.required);
  const canDismiss = required.every((t) => t.done);
  // Closed by the shop: it stays closed unless something required comes undone.
  if (phase === "first_sale" && s.onboardingDismissed && canDismiss) return null;
  return { phase, tasks, done: tasks.filter((t) => t.done).length, canDismiss, required: { done: required.filter((t) => t.done).length, total: required.length } };
}
